import { useGetResellers } from 'lib/api/superadmin/resellerQuery';
import { ButtonSwitch } from 'lib/components';
import {
  Flex,
  Gap,
  HeaderWrapper,
  MainWrapper,
} from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import { DEFAULT_PAGE } from 'lib/const/PaginationConstants';
import React, { useState } from 'react';
import { Button } from 'react-covideo-common';
import {
  ACTIVITY_OPTIONS,
  ACTIVITY_VALUES,
  ActivityValue,
  AUTOCOMPLETE_DATA_LIMIT,
  DEFAULT_DESIGNS_SIZE,
  getActiveStatus,
  LP_BUILDER_FORM_MODE,
  LP_BUILDER_MODAL_STEPS,
  LP_DESIGN_CATEGORIES,
  LpBuilderFormMode,
} from '../constants';
import { LandingPageDesignList } from './LandingPageDesignList';
import { ButtonSwitchLabel } from '../styles';
import { LandingPageDesignFilterSection } from './LandingPageDesignFilterSection';
import {
  ILandingPageDesignsQueryParams,
  useQueryLandingPageDesigns,
} from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPageDesigns';
import { MainContainer } from '../../index.styled';
import { useIsMutating } from 'react-query';
import { landingPageDesignsKeys } from 'lib/api/superadmin/landingPageDesigns/landingPageDesignsKeys';
import { LandingPageBuilderModalForm } from '../components/modal/LandingPageBuilderModalForm';
import { HiPlus } from 'react-icons/hi';
import {
  getLandingPageDesignsAutocomplete,
  IAutocompleteResponse,
} from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPagesAutocomplete';
import { useQueryGetAllGroups } from 'lib/api/superadmin/landingPageDesignGroups/useQueryGetAllGroups';
import {
  IGroupOption,
  ILandingPageBuilderForm,
  ILandingPageDesignGroup,
  ILandingPageDesignResponse,
  IResellerOption,
  ITemplateOption,
  LP_BUILDER_MODAL_STEP,
} from '../types';
import { SelectedLandingPageDesignsRibbon } from '../components/SelectedLandingPageDesignsRibbon';
import { MdGroup } from 'react-icons/md';
import { useHistory } from 'react-router';
import { useEditLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useEditLandingPageDesignMutation';
import { useCreateLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useCreateLandingPageDesignMutation';
import { usePrepareLandingPageData } from '../helpers/usePrepareLandingPageData';
import { useDeleteLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useDeleteLandingPageDesignMutation';

export const LandingPagesV2 = () => {
  const history = useHistory();
  // query
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = React.useState(DEFAULT_DESIGNS_SIZE);
  const [activityValue, setActivityValue] = useState<ActivityValue>(
    ACTIVITY_VALUES.all
  );
  // templates
  const [selectedTemplate, setSelectedTemplate] =
    useState<LP_DESIGN_CATEGORIES | null>(null);
  // resellers
  const [selectedResellers, setSelectedResellers] = useState<
    (string | number)[]
  >([]);
  // groups
  const [selectedGroups, setSelectedGroups] = useState<(string | number)[]>([]);

  // template design
  const [selectedTemplateDesignId, setSelectedTemplateDesignId] = useState<
    number | null
  >(null);
  const [batchEditTemplates, setBatchEditTemplates] = useState<
    ILandingPageDesignResponse[]
  >([]);
  //autocomplete
  const [isFetchingAutocompleteData, setIsFetchingAutocompleteData] =
    useState(false);
  // modal
  const [step, setStep] = useState<LP_BUILDER_MODAL_STEP>(
    LP_BUILDER_MODAL_STEPS.design
  );
  // form mode
  const [modalState, setModalState] = useState<{
    mode: LpBuilderFormMode;
    open: boolean;
  }>({
    mode: LP_BUILDER_FORM_MODE.CREATE,
    open: false,
  });
  const [previewLandingPageId, setPreviewLandingPageId] = useState(0);

  // DELETE Design Mutation
  const { mutateAsync: deleteDesignMutation, isLoading: isDeletingPreview } =
    useDeleteLandingPageDesignMutation({
      showToastNotifications: false,
    });

  const deleteAndResetPreviewLandingPageDesign = async () => {
    await deleteDesignMutation({ templateId: previewLandingPageId });
    setPreviewLandingPageId(0);
  };

  const isDuplicating =
    useIsMutating({
      mutationKey: landingPageDesignsKeys.duplicate(),
    }) > 0;

  // GET Resellers
  const resellersQueryParams = {
    search: '',
    limit: 500,
    enabled: true,
  };

  const { data: resellers, isFetching: isFetchingResellers } =
    useGetResellers(resellersQueryParams);

  const { data: groups, isFetching: isFetchingGroups } = useQueryGetAllGroups();

  const resellerOptions: IResellerOption[] = resellers
    ? Array.from(
        new Map(
          resellers[0].map(reseller => [
            reseller.resellerId,
            {
              label: `${reseller.resellerName} - ${reseller.resellerId}`,
              value: reseller.resellerId,
            } as IResellerOption,
          ])
        ).values()
      )
    : [];

  const groupOptions: IGroupOption[] = groups
    ? Array.from(
        new Map(
          groups.map((group: ILandingPageDesignGroup) => [
            group.landingPageDesignGroupId,
            {
              label: `${group.title} - ${group.landingPageDesignGroupId}`,
              value: group.landingPageDesignGroupId,
            } as IGroupOption,
          ])
        ).values()
      )
    : [];

  const params: ILandingPageDesignsQueryParams = {
    start: page * (limit || DEFAULT_DESIGNS_SIZE),
    limit: limit || DEFAULT_DESIGNS_SIZE,
    search: searchQuery,
    category: selectedTemplate,
    resellerIds: selectedResellers as number[],
    groupIds: selectedGroups as number[],
    active: getActiveStatus(activityValue),
  };

  // GET Landing Page Designs
  const {
    data: landingPageDesignsData,
    isLoading: isLoadingLandingPageDesignsData,
    isFetching: isFetchingLandingPageDesignsData,
  } = useQueryLandingPageDesigns(params);

  const count = landingPageDesignsData?.count || 0;

  const isLoadingLandingPagesDesigns =
    isLoadingLandingPageDesignsData || isFetchingLandingPageDesignsData;

  // Template options
  const templateOptions: ITemplateOption[] = [
    { value: null, label: 'All Templates' },
    ...Object.values(LP_DESIGN_CATEGORIES).map(value => ({
      value,
      label: value === 'Oem Template' ? 'OEM Templates' : `${value}s`,
    })),
  ];

  const onCloseLandingPageBuilderModalHandler = async () => {
    if (!!previewLandingPageId && !selectedTemplateDesignId) {
      await deleteAndResetPreviewLandingPageDesign();
    }
    setStep(LP_BUILDER_MODAL_STEPS.design);
    setModalState(s => ({ ...s, open: false }));
    setSelectedTemplateDesignId(null);
    setPreviewLandingPageId(0);
  };

  // EDIT Design Mutation
  const {
    mutateAsync: editDesignMutation,
    isLoading: isLoadingEditDesignMutation,
  } = useEditLandingPageDesignMutation(onCloseLandingPageBuilderModalHandler);

  // CREATE Design Mutation
  const {
    mutateAsync: createDesignMutation,
    isLoading: isCreatingEditDesignMutation,
  } = useCreateLandingPageDesignMutation(onCloseLandingPageBuilderModalHandler);

  const { prepare } = usePrepareLandingPageData();

  const onUpsertSubmit = async (values: ILandingPageBuilderForm) => {
    const payload = await prepare(values);

    selectedTemplateDesignId
      ? await editDesignMutation({
          id: selectedTemplateDesignId,
          ...payload,
        })
      : await createDesignMutation(payload);
  };

  const fetchSearchAutocompleteData = async (
    search: string
  ): Promise<IAutocompleteResponse[]> => {
    setIsFetchingAutocompleteData(true);
    try {
      const result = await getLandingPageDesignsAutocomplete({
        search,
        limit: AUTOCOMPLETE_DATA_LIMIT,
      });
      return result;
    } finally {
      setIsFetchingAutocompleteData(false);
    }
  };

  const onLandingPagesSearchHandler = (value: string) => {
    setSearchQuery(value);
    setPage(DEFAULT_PAGE);
  };

  const onPaginationChange = async ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setPage(newPage);
    setLimit(newSize);
  };

  const handleOpenCreateModal = () => {
    setModalState({
      mode: LP_BUILDER_FORM_MODE.CREATE,
      open: true,
    });
  };

  const isSubmitting =
    isLoadingEditDesignMutation ||
    isCreatingEditDesignMutation ||
    isDeletingPreview;

  return (
    <MainContainer>
      <MainWrapper resetPadding={false} noSidePadding>
        <Flex gap='40px'>
          <HeaderWrapper m='0px'>
            <Heading>Landing Pages</Heading>
            <Gap gap='8px'>
              <Button
                variant='secondary'
                text='Manage Groups'
                disabled={isLoadingLandingPagesDesigns || isDuplicating}
                icon={<MdGroup />}
                onClick={() => {
                  history.push('/admin/landing-pages-v2/manage-groups');
                }}
              />
              <Button
                text={'Add New Design'}
                onClick={handleOpenCreateModal}
                disabled={isLoadingLandingPagesDesigns || isDuplicating}
                icon={<HiPlus />}
              />
            </Gap>
          </HeaderWrapper>
          <LandingPageDesignFilterSection
            templateOptions={templateOptions}
            selectedTemplate={selectedTemplate}
            resellerOptions={resellerOptions}
            setSelectedTemplate={setSelectedTemplate}
            setSelectedResellers={setSelectedResellers}
            onLandingPagesSearchHandler={onLandingPagesSearchHandler}
            searchQuery={searchQuery}
            isFetchingResellers={isFetchingResellers}
            fetchSearchAutocompleteData={fetchSearchAutocompleteData}
            isFetchingAutocompleteData={isFetchingAutocompleteData}
            setSelectedGroups={setSelectedGroups}
            groupOptions={groupOptions}
            isFetchingGroups={isFetchingGroups}
          />
          <Flex
            flexDirection='row'
            justifyContent='space-between'
            margin='0px 0px 16px 0px'
          >
            <Heading>Landing Page Templates</Heading>
            <Flex gap='8px' width='auto'>
              <ButtonSwitchLabel>SHOW:</ButtonSwitchLabel>
              <ButtonSwitch
                className='btn-switch'
                defaultValue={ACTIVITY_VALUES.all}
                onChange={value => setActivityValue(value as ActivityValue)}
                values={ACTIVITY_OPTIONS}
                disabled={isLoadingLandingPagesDesigns || isDuplicating}
              />
            </Flex>
          </Flex>
        </Flex>

        {modalState.open && (
          <LandingPageBuilderModalForm
            onSubmit={values =>
              onUpsertSubmit(values as ILandingPageBuilderForm)
            }
            isSubmitting={isSubmitting}
            mode={modalState.mode}
            selectedTemplateDesignId={selectedTemplateDesignId}
            previewLandingPageId={previewLandingPageId}
            setPreviewLandingPageId={setPreviewLandingPageId}
            setStep={setStep}
            step={step}
            onClose={onCloseLandingPageBuilderModalHandler}
          />
        )}

        <LandingPageDesignList
          data={landingPageDesignsData?.data || []}
          count={count}
          isLoading={isLoadingLandingPagesDesigns}
          searchQuery={searchQuery}
          onPaginationChange={onPaginationChange}
          page={page}
          limit={limit}
          setModalState={setModalState}
          setSelectedTemplateDesignId={setSelectedTemplateDesignId}
          setStep={setStep}
          batchEditTemplates={batchEditTemplates}
          setBatchEditTemplates={setBatchEditTemplates}
        />

        <SelectedLandingPageDesignsRibbon
          selectedTemplates={batchEditTemplates}
          setSelectedTemplates={setBatchEditTemplates}
          landingPageDesigns={landingPageDesignsData?.data || []}
          showAddToGroup
        />
      </MainWrapper>
    </MainContainer>
  );
};
